import type { AnyExtension, Editor, EditorEvents as TiptapEditorEvents, JSONContent, DocumentType } from '@tiptap/core'
import type { SuggestionOptions } from '@tiptap/suggestion'
import type { EditorProps } from '@tiptap/pm/view'
import type { EditorState } from '@tiptap/pm/state'
import type { TmEditorOutputPlugin } from '@/services/forms/tmTiptap/outputPlugins/types'
import type TmTiptapEditorExtensionsSet from '@/services/forms/tmTiptap/tmTiptapEditorExtensionsSet'
// eslint-disable-next-line tp/forbid-import-composable-to-service,tp/using-vue-in-services-restriction
import type { ComponentPublicInstance } from '@/composition/vue/compositionApi'
import type { IconName } from '@/assets/icons/icons'
import type { TmTiptapEditor as TmTiptapEditorImport } from '@/services/forms/tmTiptap/tmTiptapEditor'
import type { EmailEditorModes } from '@/services/forms/tmTiptap/extensions/codemirror/types'
import type { tmEmailEditorPreserveHtmlDocumentName } from '@/services/forms/tmTiptap/extensions/email/tmEmailEditorPreserveHtmlDocument'
import type {
  tmCodemirrorEditorExtensionName,
  TmCodemirrorEditorExtensionStorage,
} from '@/services/forms/tmTiptap/extensions/codemirror/tmCodemirrorEditorExtension'

export type AnyExtensionOrOutputPlugin = AnyExtension | TmEditorOutputPlugin
export type ExtensionsOrOutputPlugins = AnyExtensionOrOutputPlugin[]
export type EditorValue = JSONContent
export const editorLineHeight = 22
export type Emoji = {
  colons: string
  emoticons: string[]
  id: string
  name: string
  native: EmojiNative
  // eslint-disable-next-line @typescript-eslint/naming-convention
  short_name: string
  // eslint-disable-next-line @typescript-eslint/naming-convention
  short_names: string[]
  skin: string | null
  unified: string

  getPosition: () => string
}
export type EmojiNative = string
// tags
export type Tag = {
  title: string
  icon: IconName
}
export type TagOptions = {
  suggestion: (editor: Editor, suggestTagsDropdown: TagSuggestInstance) => SuggestionOptions
  plainMode: boolean
  HTMLAttributes: {
    class: string
    // eslint-disable-next-line @typescript-eslint/naming-convention
    'data-type': string
  }
}
export type TagStorage = {
  suggestionIsOpen: boolean
  tagsValidation: boolean
  availableTags: Tag[]
  suggestTagsDropdown: TagSuggestInstance | null
}
export type TagSuggestProps = {
  target?: HTMLElement
  focusCallback?: () => void
  selectCallback: (tag: string) => void
}
export type TagSuggestMethods = {
  toggleVisibility: (val: boolean) => void
  updateActiveTag: (tag: HTMLElement) => void
  updateList: (items: Tag[]) => void
  onKeydown: (e: KeyboardEvent) => void
}

export interface TagSuggestInstance extends ComponentPublicInstance, TagSuggestMethods {
  $el: HTMLElement
}

export type TagEditorState = EditorState & {
  tag$: {
    active: boolean
    composing: boolean
    decorationId: string
    query: string
    text: string
    range: {
      from: number
      to: number
    }
  }
}
// link
export type LinkOptions = {
  HTMLAttributes: {
    class: string
    // eslint-disable-next-line @typescript-eslint/naming-convention
    'data-type': string
  }
}
export type TmEditorOptions = {
  element: Element
  editorId: string
  extensions: TmTiptapEditorExtensionsSet
  initialContent?: string | JSONContent
  editable?: boolean
  tabindex?: string
  editorProps?: EditorProps
  replaceLinksWithSpan?: boolean
  hooks?: {
    onBeforeCreate?: (props: TiptapEditorEvents['beforeCreate']) => void
    onCreate?: (props: TiptapEditorEvents['create']) => void
    onUpdate?: (props: TiptapEditorEvents['update']) => void
    onFocus?: EditorFocusHandler
    onBlur?: EditorBlurHandler
  }
}
export type EditorFocusHandler = (props: TiptapEditorEvents['focus']) => void
export type EditorBlurHandler = (props: TiptapEditorEvents['blur']) => void
export type EditorSubscriptionEventPayload = {
  editorId: string
}
export type TmDomOutputSpec = [string, Record<string, any>, ...unknown[]]

export const enum EditorSubscriptionEvents {
  IMAGE_UPLOAD_START = 'IMAGE_UPLOAD_START',
  IMAGE_UPLOAD_END = 'IMAGE_UPLOAD_END',
}

declare module '@tiptap/core' {
  interface Storage {
    [tmEmailEditorPreserveHtmlDocumentName]: {
      htmlDocument: Document | null
      extractedStyles: string[]
    }
    [tmCodemirrorEditorExtensionName]: TmCodemirrorEditorExtensionStorage
    [key: string]: unknown
  }

  interface EditorEvents {
    // tmImageResizeExtension
    imageResizeActivate: { editor: TmTiptapEditorImport; pos: any; htmlImage: HTMLImageElement }
    imageResizeDeactivate: Record<string, never>

    // tmCodemirrorEditorExtension
    updateCodemirrorEditable: { isEditable: boolean }
    updateWysiwygActiveMode: { mode: EmailEditorModes }
    updateWysiwygEditorContent: { html: string }
    updateCodemirrorContent: { html: string }
  }
}

export type JSONContentOrDocument = JSONContent | DocumentType
